/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/i18n/config.ts
 * PURPOSE: Configuration and environment settings
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: Frontend Application, Vietnamese F&B Platform
 * - Impact: Application Quality, User Experience, Performance
 * - Stakeholders: Development Team, End Users, Product Team
 *
 * @technical-architecture
 * - Layer: Frontend Application Layer
 * - Pattern: React Application, TypeScript, Modern Frontend
 * - Dependencies: React 18, TypeScript, Vite, TailwindCSS
 */

// Detect if we're in Vue or React environment
const isVueApp = typeof window !== 'undefined' && window.__VUE__;

// React imports (conditional)
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Shared language detection logic
export const getSavedLanguage = (): string => {
  // Check localStorage first
  const savedLang = localStorage.getItem('nqh-language');
  if (savedLang && ['vi', 'en'].includes(savedLang)) return savedLang;
  
  // Check user preference from auth state
  const authData = localStorage.getItem('auth');
  if (authData) {
    try {
      const { user } = JSON.parse(authData);
      if (user?.language_preference) {
        return user.language_preference;
      }
    } catch (_error) {
      // Ignore parse errors
    }
  }
  
  // Check browser language
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('vi')) return 'vi';
  
  // Default to Vietnamese per business requirement
  return 'vi';
};

i18n
  // Load translations using http backend
  .use(HttpBackend)
  // Detect user language
  .use(LanguageDetector)
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    // Default language
    lng: getSavedLanguage(),
    // Fallback language when translation is missing
    fallbackLng: 'vi',
    // Debug mode (only in development)
    debug: import.meta.env.DEV,
    
    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    
    // Backend options
    backend: {
      loadPath: '/locales/{{lng}}/translation.json',
      addPath: '/locales/add/{{lng}}/{{ns}}', // For missing key collection
    },
    
    // Language detector options
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    
    // React options
    react: {
      useSuspense: true,
    },
    
    // Namespace settings
    defaultNS: 'translation',
    ns: ['translation'],
    
    // Resource loading
    load: 'languageOnly', // Load 'vi' instead of 'vi-VN'
    
    // Missing key handler (development only)
    saveMissing: import.meta.env.DEV,
  missingKeyHandler: (_lng, _ns, _key) => {
      if (import.meta.env.DEV) {
      }
    },
  });

// Shared language change function
export const changeLanguage = async (lng: string) => {
  if (!['vi', 'en'].includes(lng)) return;
  
  // Change in i18next (React)
  await i18n.changeLanguage(lng);
  
  // Save preference
  localStorage.setItem('nqh-language', lng);
  document.documentElement.lang = lng;
  
  // Update user preference in backend if authenticated
  const authData = localStorage.getItem('auth');
  if (authData) {
    try {
      const { token } = JSON.parse(authData);
      if (token) {
        // Call API to update user language preference
        fetch('/api/v1/users/me/language', {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ language_preference: lng }),
        }).catch(_error => {
          // Removed console.log);
        });
      }
    } catch (_error) {
      // Ignore errors
    }
  }
};

// Format helpers (shared between React and Vue)
export const formatCurrency = (amount: number, lang?: string) => {
  const locale = lang || getSavedLanguage();
  if (locale === 'vi') {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0
    }).format(amount);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount / 23000);
};

export const formatDateTime = (date: Date | string, lang?: string) => {
  const locale = lang || getSavedLanguage();
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (locale === 'vi') {
    return new Intl.DateTimeFormat('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(d);
  }
  
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(d);
};

export const formatPhone = (phone: string) => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('84')) {
    const local = '0' + cleaned.slice(2);
    return local.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
  }
  if (cleaned.startsWith('0')) {
    return cleaned.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
  }
  return phone;
};

// Export configured i18n instance
export default i18n;